import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Logo } from '../components/Logo';
import './Login.css';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSettingNewPassword, setIsSettingNewPassword] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  
  const { login, handleNewPassword, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedReturn = searchParams.get('returnTo');
  const returnTo = requestedReturn?.startsWith('/') && !requestedReturn.startsWith('//') ? requestedReturn : '/dashboard';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      if (isSettingNewPassword) {
        await handleNewPassword(newPassword);
        navigate(returnTo);
        return;
      }

      if (isConfirming) {
        const { confirmSignUp, signIn } = await import('aws-amplify/auth');
        await confirmSignUp({ username: email, confirmationCode });
        await signIn({ username: email, password });
        // After successful sign in, check auth via context
        const { fetchAuthSession } = await import('aws-amplify/auth');
        await fetchAuthSession();
        navigate(returnTo);
        return;
      }

      if (isForgotPassword && !isResettingPassword) {
        const { resetPassword } = await import('aws-amplify/auth');
        await resetPassword({ username: email });
        setIsResettingPassword(true);
        setError(null);
        return;
      }

      if (isResettingPassword) {
        const { confirmResetPassword } = await import('aws-amplify/auth');
        await confirmResetPassword({ username: email, confirmationCode, newPassword });
        setIsForgotPassword(false);
        setIsResettingPassword(false);
        setConfirmationCode('');
        setNewPassword('');
        setError('Password reset successfully! Please sign in with your new password.');
        return;
      }

      const result = await login({ username: email, password });
      
      if (result.nextStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setIsSettingNewPassword(true);
      } else if (result.nextStep === 'CONFIRM_SIGN_UP') {
        setIsConfirming(true);
        setError('Please verify your email address to continue.');
      } else if (result.nextStep === 'DONE') {
        localStorage.removeItem('activeTenantId');
        navigate(returnTo);
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'An error occurred during sign in.');
    }
  };

  return (
    <div className="login">
      <div className="login__hero">
        <div className="login__hero-content">
          <Logo className="login__hero-logo" />
          <h1 className="login__hero-title">MothNode Dashboard</h1>
        </div>
      </div>
      <div className="login__form-container">
        <div className="login__card">
        <h1 className="login__title">
          {isSettingNewPassword 
            ? 'Change Password' 
            : isConfirming 
              ? 'Verify Email' 
              : isForgotPassword 
                ? 'Reset Password' 
                : 'Sign In'}
        </h1>
        
        {isSettingNewPassword && (
          <p className="login__subtitle">
            Your administrator has required you to change your temporary password before signing in.
          </p>
        )}

        {isConfirming && (
          <p className="login__subtitle">
            We sent a verification code to your email. Please enter it below.
          </p>
        )}

        {isForgotPassword && !isResettingPassword && (
          <p className="login__subtitle">
            Enter your email address and we'll send you a verification code to reset your password.
          </p>
        )}

        {isResettingPassword && (
          <p className="login__subtitle">
            We sent a verification code to your email. Please enter it along with your new password.
          </p>
        )}

        <form className="login__form" onSubmit={handleSubmit}>
          {error && <div className="login__error">{error === 'Password reset successfully! Please sign in with your new password.' ? <span style={{color: 'var(--color-status-good)'}}>{error}</span> : error}</div>}

          {!isSettingNewPassword && !isConfirming && !isForgotPassword ? (
            <>
              <div className="login__field">
                <label htmlFor="email" className="login__label">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className="login__input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>

              <div className="login__field">
                <label htmlFor="password" className="login__label">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  className="login__input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
            </>
          ) : isSettingNewPassword || isResettingPassword ? (
            <>
              {isResettingPassword && (
                <div className="login__field">
                  <label htmlFor="confirmationCode" className="login__label">
                    Verification Code
                  </label>
                  <input
                    id="confirmationCode"
                    type="text"
                    className="login__input"
                    placeholder="Enter verification code"
                    value={confirmationCode}
                    onChange={(e) => setConfirmationCode(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
              )}
              <div className="login__field">
                <label htmlFor="newPassword" className="login__label">
                  New Password
                </label>
                <input
                  id="newPassword"
                  type="password"
                  className="login__input"
                  placeholder="Enter a strong new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isLoading}
                  required
                />
                <p className="login__help">
                  Must be at least 8 characters, with uppercase, lowercase, and numbers.
                </p>
              </div>
            </>
          ) : isForgotPassword && !isResettingPassword ? (
             <div className="login__field">
                <label htmlFor="email" className="login__label">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className="login__input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  required
                />
              </div>
          ) : (
            <div className="login__field">
              <label htmlFor="confirmationCode" className="login__label">
                Verification Code
              </label>
              <input
                id="confirmationCode"
                type="text"
                className="login__input"
                placeholder="Enter verification code"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                disabled={isLoading}
                required
              />
            </div>
          )}

          <button type="submit" className="login__submit" disabled={isLoading}>
            {isLoading 
              ? 'Loading...' 
              : isSettingNewPassword || isResettingPassword
                ? 'Update Password & Sign In' 
                : isConfirming 
                  ? 'Verify & Sign In'
                  : isForgotPassword
                    ? 'Send Code'
                    : 'Sign In'}
          </button>
        </form>

        {!isSettingNewPassword && !isConfirming && !isForgotPassword && (
          <a href="#" className="login__forgot" onClick={(e) => { e.preventDefault(); setIsForgotPassword(true); setError(null); }}>
            Forgot password?
          </a>
        )}

        {isForgotPassword && (
          <a href="#" className="login__forgot" style={{ marginTop: '1rem', display: 'block', textAlign: 'center' }} onClick={(e) => { e.preventDefault(); setIsForgotPassword(false); setIsResettingPassword(false); setError(null); }}>
            Back to Sign In
          </a>
        )}
      </div>
      </div>
    </div>
  );
}

export default Login;
